import React, { useEffect } from 'react';
import { useAdminAuthStore } from '../stores/authStore';
import { useDashboardStore } from '../stores/dashboardStore';
import { FaUsers, FaVideo, FaFilm, FaServer } from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function DashboardPage() {
  const accessToken = useAdminAuthStore((state) => state.accessToken);
  const stats = useDashboardStore((state) => state.stats);
  const systemHealth = useDashboardStore((state) => state.systemHealth);
  const getStats = useDashboardStore((state) => state.getStats);
  const getSystemHealth = useDashboardStore((state) => state.getSystemHealth);
  const isLoading = useDashboardStore((state) => state.isLoading);

  useEffect(() => {
    if (accessToken) {
      getStats(accessToken);
      getSystemHealth(accessToken);
    }
  }, [accessToken]);

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="dashboard-card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value || 0}</p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon size={32} className="text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome to the vSkul admin dashboard</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={FaUsers}
              label="Total Users"
              value={stats?.total_users}
              color="bg-blue-500"
            />
            <StatCard
              icon={FaVideo}
              label="Active Rooms"
              value={stats?.active_rooms}
              color="bg-green-500"
            />
            <StatCard
              icon={FaFilm}
              label="Recordings"
              value={stats?.total_recordings}
              color="bg-purple-500"
            />
            <StatCard
              icon={FaServer}
              label="Uptime"
              value={stats?.uptime_percentage ? `${stats.uptime_percentage}%` : 'N/A'}
              color="bg-orange-500"
            />
          </div>

          {/* System Health */}
          {systemHealth && (
            <div className="dashboard-card mb-8">
              <div className="dashboard-card-header">
                <h2 className="text-xl font-bold">System Health</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-gray-600 text-sm">Laravel Backend</p>
                  <p className="text-2xl font-bold mt-2">
                    {systemHealth.laravel_backend ? (
                      <span className="text-green-600">✓ Online</span>
                    ) : (
                      <span className="text-red-600">✗ Offline</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">Node.js SFU</p>
                  <p className="text-2xl font-bold mt-2">
                    {systemHealth.nodejs_sfu ? (
                      <span className="text-green-600">✓ Online</span>
                    ) : (
                      <span className="text-red-600">✗ Offline</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">Database</p>
                  <p className="text-2xl font-bold mt-2">
                    {systemHealth.database ? (
                      <span className="text-green-600">✓ Online</span>
                    ) : (
                      <span className="text-red-600">✗ Offline</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Activity Chart */}
          {stats?.activity_data && (
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h2 className="text-xl font-bold">User Activity (Last 7 days)</h2>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.activity_data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="activeUsers" stroke="#3b82f6" />
                  <Line type="monotone" dataKey="newUsers" stroke="#10b981" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
